import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRegUserCircle } from 'react-icons/fa';
import { supabase } from '../../utils/supabaseClient';
import { useProfile } from '../../contexts/ProfileContext';
import { useTheme } from '../../contexts/ThemeContext';
import { hardResetAuth } from '../../utils/supabaseAuthSafe';
import DeleteAccountSection from './DeleteAccountSection';
import {
  MAX_PLAYER_NAME_LENGTH,
  validatePlayerName,
} from '../../utils/playerNameValidation';
import './UserMenu.css';

const UserMenu = ({ hideSignedInName = false }) => {
  const { user, profile, updateUsername } = useProfile();
  const { theme, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [playerNameDraft, setPlayerNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const accountName = useMemo(() => {
    const profileName = profile?.username;
    if (typeof profileName === 'string' && profileName.trim()) {
      return profileName.trim();
    }
    return 'Player';
  }, [profile?.username]);

  const signedInEmail = useMemo(() => {
    if (!user) return '';
    const authEmail = typeof user.email === 'string' ? user.email.trim() : '';
    if (authEmail) return authEmail;
    const metadataEmail =
      typeof user.user_metadata?.email === 'string'
        ? user.user_metadata.email.trim()
        : '';
    return metadataEmail;
  }, [user]);

  const displayLabel = useMemo(() => {
    if (!user) return 'Sign In';
    if (hideSignedInName) return '';
    return accountName;
  }, [accountName, hideSignedInName, user]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setIsEditingName(false);
    setPlayerNameDraft('');
    setNameError('');
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleStartNameEdit = () => {
    setPlayerNameDraft(profile?.username || accountName || '');
    setNameError('');
    setIsEditingName(true);
  };

  const handleCancelNameEdit = () => {
    setIsEditingName(false);
    setPlayerNameDraft('');
    setNameError('');
  };

  const handleSaveName = async () => {
    if (isSavingName) return;

    const { isValid, normalizedName, error } = validatePlayerName(playerNameDraft);
    if (!isValid) {
      setNameError(error);
      return;
    }

    setIsSavingName(true);
    const { success, error: updateError } = await updateUsername(normalizedName);
    setIsSavingName(false);

    if (success) {
      setIsEditingName(false);
      setPlayerNameDraft('');
      setNameError('');
      return;
    }

    setNameError(updateError || 'Unable to update player name.');
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await hardResetAuth(supabase, { revokeRemote: true, remoteTimeoutMs: 4000 });
    } catch (error) {
      console.error('Sign out failed:', error.message || error);
    }
    setIsSigningOut(false);
    setIsOpen(false);
    navigate('/');
  };

  const handleSignIn = () => {
    setIsOpen(false);
    navigate('/auth');
  };

  const handleAccountDeleted = async () => {
    try {
      await hardResetAuth(supabase, { revokeRemote: false });
    } catch (error) {
      console.error('Post-delete sign out failed:', error?.message || error);
    }

    setIsOpen(false);
    navigate('/auth', { replace: true });
  };

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className={`user-menu-button header-pill-button${displayLabel ? '' : ' user-menu-button--icon-only'}`}
        onClick={handleToggle}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={user ? `${accountName} account menu` : 'Sign In'}
      >
        <FaRegUserCircle className="user-menu-icon" aria-hidden="true" focusable="false" />
        {displayLabel && <span className="user-menu-label">{displayLabel}</span>}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-name">{user ? accountName : 'Guest'}</div>
            <div className="user-menu-subtitle">
              {user ? 'Signed in' : 'Guest'}
            </div>
            {user && signedInEmail && (
              <div className="user-menu-email">{signedInEmail}</div>
            )}
          </div>
          <div className="user-menu-actions">
            {/* Theme toggle — available for everyone */}
            <button
              className="user-menu-action user-menu-theme-toggle"
              onClick={toggleTheme}
              type="button"
            >
              {theme === 'light' ? '\uD83C\uDF19 Dark Mode' : '\u2600\uFE0F Light Mode'}
            </button>
            {user ? (
              <>
                {!isEditingName ? (
                  <button
                    className="user-menu-action"
                    onClick={handleStartNameEdit}
                    type="button"
                  >
                    Change player name
                  </button>
                ) : (
                  <div className="user-menu-edit">
                    <input
                      className="user-menu-input"
                      type="text"
                      value={playerNameDraft}
                      onChange={(event) => setPlayerNameDraft(event.target.value)}
                      placeholder="Enter player name"
                      maxLength={MAX_PLAYER_NAME_LENGTH}
                      disabled={isSavingName}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleSaveName();
                        }
                      }}
                    />
                    {nameError && <div className="user-menu-error">{nameError}</div>}
                    <div className="user-menu-edit-actions">
                      <button
                        className="user-menu-action"
                        onClick={handleSaveName}
                        type="button"
                        disabled={isSavingName}
                      >
                        {isSavingName ? 'Saving...' : 'Save name'}
                      </button>
                      <button
                        className="user-menu-action user-menu-action-secondary"
                        onClick={handleCancelNameEdit}
                        type="button"
                        disabled={isSavingName}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <button
                  className="user-menu-action user-menu-action-danger"
                  onClick={handleSignOut}
                  type="button"
                  disabled={isSigningOut}
                >
                  {isSigningOut ? 'Logging out...' : 'Log out'}
                </button>
                <DeleteAccountSection onDeleted={handleAccountDeleted} />
              </>
            ) : (
              <button
                className="user-menu-action"
                onClick={handleSignIn}
                type="button"
              >
                Sign Up / Login
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
